/*
 * Collection page filters, sorting and pagination.
 *
 * Every change re-renders the section through the Section Rendering API and
 * swaps it in place, so the page never reloads. Open groups, expanded
 * "Show more" lists, the drawer and keyboard focus survive each swap.
 */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 990px)');

  class LsCollection {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.controller = null;
      this.closedGroups = new Set();
      this.expandedGroups = new Set();
      this.lastTrigger = null;

      root.addEventListener('change', (event) => this.onChange(event));
      root.addEventListener('click', (event) => this.onClick(event));
      root.addEventListener('toggle', (event) => this.onToggle(event), true);
      root.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer?.classList.contains('is-open')) this.closeDrawer();
      });
      DESKTOP.addEventListener('change', () => this.closeDrawer(false));
    }

    get drawer() {
      return this.root.querySelector('[data-ls-plp-drawer]');
    }

    get form() {
      return this.root.querySelector('[data-ls-plp-form]');
    }

    onChange(event) {
      const target = event.target;
      if (!target.matches('[data-ls-plp-form] input[type="checkbox"], [data-ls-plp-sort]')) return;

      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') params.append(key, value);
      }
      const query = params.toString();
      this.render(`${window.location.pathname}${query ? `?${query}` : ''}`);
    }

    onClick(event) {
      const link = event.target.closest('a[data-ls-plp-link]');
      if (link) {
        event.preventDefault();
        this.render(link.getAttribute('href'), { scroll: link.hasAttribute('data-ls-plp-scroll') });
        return;
      }

      const more = event.target.closest('[data-ls-plp-more]');
      if (more) {
        const group = more.closest('[data-ls-plp-group]');
        const expanded = !group.classList.contains('is-expanded');
        this.setExpanded(group, expanded);
        if (expanded) this.expandedGroups.add(group.dataset.lsPlpGroup);
        else this.expandedGroups.delete(group.dataset.lsPlpGroup);
        return;
      }

      const open = event.target.closest('[data-ls-plp-open]');
      if (open) {
        this.openDrawer(open);
        return;
      }

      if (event.target.closest('[data-ls-plp-close]')) this.closeDrawer();
    }

    onToggle(event) {
      const group = event.target;
      if (!group.matches?.('[data-ls-plp-group]')) return;
      if (group.open) this.closedGroups.delete(group.dataset.lsPlpGroup);
      else this.closedGroups.add(group.dataset.lsPlpGroup);
    }

    setExpanded(group, expanded) {
      const button = group.querySelector('[data-ls-plp-more]');
      group.classList.toggle('is-expanded', expanded);
      if (!button) return;
      button.setAttribute('aria-expanded', String(expanded));
      button.textContent = expanded ? button.dataset.less : button.dataset.more;
    }

    openDrawer(trigger) {
      const drawer = this.drawer;
      if (!drawer || DESKTOP.matches) return;
      this.lastTrigger = trigger || null;
      drawer.classList.add('is-open');
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      this.root.querySelector('[data-ls-plp-open]')?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('ls-plp-locked');
      drawer.querySelector('.ls-plp__close')?.focus();
    }

    closeDrawer(restoreFocus = true) {
      const drawer = this.drawer;
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.removeAttribute('role');
      drawer.removeAttribute('aria-modal');
      const opener = this.root.querySelector('[data-ls-plp-open]');
      opener?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('ls-plp-locked');
      if (restoreFocus) opener?.focus();
    }

    async render(url, { scroll = false, push = true } = {}) {
      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.delete('section_id');
      const pageUrl = `${requestUrl.pathname}${requestUrl.search}`;
      requestUrl.searchParams.set('section_id', this.sectionId);

      this.controller?.abort();
      this.controller = new AbortController();
      this.root.classList.add('is-loading');

      let html;
      try {
        const response = await fetch(requestUrl, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        html = await response.text();
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = pageUrl;
        return;
      }

      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ls-plp]');
      if (!fresh) {
        window.location.href = pageUrl;
        return;
      }

      const drawerOpen = this.drawer?.classList.contains('is-open');
      const focusedId = document.activeElement?.id;
      const formScroll = this.form?.scrollTop || 0;

      this.root.innerHTML = fresh.innerHTML;
      this.root.classList.remove('is-loading');

      this.root.querySelectorAll('[data-ls-plp-group]').forEach((group) => {
        const key = group.dataset.lsPlpGroup;
        if (this.closedGroups.has(key)) group.open = false;
        if (this.expandedGroups.has(key)) this.setExpanded(group, true);
      });

      if (drawerOpen && !DESKTOP.matches) {
        this.drawer?.classList.add('is-open');
        this.drawer?.setAttribute('role', 'dialog');
        this.drawer?.setAttribute('aria-modal', 'true');
        this.root.querySelector('[data-ls-plp-open]')?.setAttribute('aria-expanded', 'true');
        if (this.form) this.form.scrollTop = formScroll;
      } else {
        document.body.classList.remove('ls-plp-locked');
      }

      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

      if (push) window.history.pushState({ lsPlp: true }, '', pageUrl);
      if (scroll) {
        const top = this.root.querySelector('.ls-plp__body').getBoundingClientRect().top + window.scrollY - 120;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      }
    }
  }

  const instances = [];
  const init = () => {
    document.querySelectorAll('[data-ls-plp]').forEach((root) => {
      if (root.lsCollection) return;
      root.lsCollection = new LsCollection(root);
      instances.push(root.lsCollection);
    });
  };

  window.addEventListener('popstate', () => {
    instances.forEach((instance) => instance.render(window.location.href, { push: false }));
  });

  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
